import { useCallback, useEffect, useState } from 'react';
import { Order, PackagingDetail, PreparationWithProducts } from '../models';
import { usePackingService } from './usePackingService';

export const usePreparationWithProducts = (id: number) => {
  const [preparation, setPreparation] = useState<PreparationWithProducts | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [packagings, setPackagings] = useState<PackagingDetail[]>([]);
  const [packagingsLoading, setPackagingsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const packingService = usePackingService();

  const fetchPreparation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Cargando preparación con ID:', id);
      const preparationData = await packingService.getPreparation(id);

      console.log('✅ Preparación cargada:', preparationData);
      setPreparation(preparationData);
    } catch (err) {
      console.error('❌ Error fetching preparation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar la preparación';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, packingService]);

  const fetchPackagingsIfNeeded = useCallback(
    async (ordersData: Order[]) => {
      const anyOrderHasPackagings = ordersData.some(
        (order) => Array.isArray(order.order_packagings) && order.order_packagings.length > 0,
      );

      if (!anyOrderHasPackagings) {
        setPackagings([]);
        return;
      }

      try {
        setPackagingsLoading(true);
        const data = await packingService.adapter.getAllPackagings();
        setPackagings(data);
      } catch (err) {
        console.error('❌ Error fetching packagings:', err);
        setPackagings([]);
      } finally {
        setPackagingsLoading(false);
      }
    },
    [packingService],
  );

  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      setOrdersError(null);

      console.log('🔍 Cargando pedidos para preparación:', id);
      const ordersData = await packingService.adapter.getOrdersByPreparationId(id);

      console.log('✅ Pedidos cargados:', ordersData);
      setOrders(ordersData);

      // Si algún pedido tiene order_packagings, obtener catálogo de packagings
      await fetchPackagingsIfNeeded(ordersData);
    } catch (err) {
      console.error('❌ Error fetching orders:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar los pedidos';
      setOrdersError(errorMessage);
    } finally {
      setOrdersLoading(false);
    }
  }, [id, packingService, fetchPackagingsIfNeeded]);

  useEffect(() => {
    if (id) {
      fetchPreparation();
      fetchOrders();
    }
  }, [fetchPreparation, fetchOrders, id]);

  // Función para calcular si una línea está completada (como en Packing original)
  const isLineCompleted = useCallback(
    (line: any) => {
      if (!orders || orders.length === 0) {
        // Si no hay pedidos, usar qty_done como fallback
        return line.qty_done >= line.qty;
      }

      // Calcular cantidad completada basada en pedidos con processed_at
      const completedFromOrders = orders.reduce((total, order) => {
        if (!order.shipment?.processed_at) return total;

        let orderCompletedQty = 0;
        (order.lines || []).forEach((orderLine) => {
          if (orderLine.pack && orderLine.pack.lines) {
            // Es un pack - multiplicar cantidad del producto × cantidad de packs
            const packQuantity = orderLine.qty || 1;
            orderLine.pack.lines.forEach((packLine) => {
              if (packLine.product?.code === line.product?.code) {
                orderCompletedQty += (packLine.qty || 0) * packQuantity;
              }
            });
          } else if (orderLine.product && orderLine.product.code === line.product?.code) {
            // Es un producto individual
            orderCompletedQty += orderLine.qty || 0;
          }
        });

        return total + orderCompletedQty;
      }, 0);

      // Una línea está completada si la cantidad de pedidos completados >= cantidad esperada
      return completedFromOrders >= line.qty;
    },
    [orders],
  );

  // Función para calcular si la preparación está completada
  const isPreparationCompleted = useCallback(() => {
    if (!preparation || !preparation.lines || preparation.lines.length === 0) {
      return false;
    }

    // Verificar si hay pedidos
    if (!orders || orders.length === 0) {
      // Si no hay pedidos, usar qty_done como fallback
      return preparation.lines.every((line) => line.qty_done >= line.qty);
    }

    // Una preparación está completada cuando todas sus líneas están completadas
    return preparation.lines.every((line) => isLineCompleted(line));
  }, [preparation, orders, isLineCompleted]);

  // Función para actualizar la cantidad de una línea específica
  const updateLineQuantity = useCallback(
    async (lineId: number, quantityToAdd: number) => {
      try {
        if (!preparation) {
          throw new Error('No hay preparación cargada');
        }

        console.log('🔍 Confirmando empaquetado:', {
          preparationId: preparation.id,
          lineId,
          quantityToAdd,
        });

        // Llamar a la API para confirmar el empaquetado
        const response = await packingService.confirmPacking(preparation.id, lineId, quantityToAdd);

        if (response.success) {
          // Actualizar el estado local
          setPreparation((prev) => {
            if (!prev) return prev;

            return {
              ...prev,
              lines: prev.lines.map((line) =>
                line.id === lineId ? { ...line, qty_done: Math.min(line.qty_done + quantityToAdd, line.qty) } : line,
              ),
            };
          });

          console.log('✅ Empaquetado confirmado exitosamente');
        } else {
          throw new Error(response.message || 'Error al confirmar el empaquetado');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al actualizar la cantidad';
        console.error('❌ Error updating line quantity:', err);
        throw new Error(errorMessage);
      }
    },
    [preparation, packingService],
  );

  // Función para actualizar la preparación completa con datos del backend
  const updatePreparation = useCallback((updatedPreparation: PreparationWithProducts) => {
    setPreparation(updatedPreparation);
  }, []);

  // Función para actualizar el estado de un pedido específico
  const updateOrderStatus = useCallback((orderId: number, newStatus: Partial<Order>) => {
    setOrders((prevOrders) => prevOrders.map((order) => (order.id === orderId ? { ...order, ...newStatus } : order)));
  }, []);

  // Función para refrescar la preparación y pedidos
  const refetch = useCallback(() => {
    fetchPreparation();
    fetchOrders();
  }, [fetchPreparation, fetchOrders]);

  // Función para actualizar solo los pedidos (sin loader)
  const refreshOrders = useCallback(async () => {
    try {
      const ordersData = await packingService.adapter.getOrdersByPreparationId(id);
      setOrders(ordersData);
      await fetchPackagingsIfNeeded(ordersData);
    } catch (err) {
      console.error('❌ Error refreshing orders:', err);
    }
  }, [id, packingService, fetchPackagingsIfNeeded]);

  return {
    preparation,
    orders,
    packagings,
    packagingsLoading,
    loading,
    ordersLoading,
    error,
    ordersError,
    isPreparationCompleted: isPreparationCompleted(),
    isLineCompleted,
    updateLineQuantity,
    updatePreparation,
    updateOrderStatus,
    refreshOrders,
    refetch,
  };
};
